import { motion } from 'framer-motion';
import { statusColor, statusLabel } from '@/lib/biomarkers';

interface MarkerData {
  key: string;
  name: string;
  value: number;
  unit: string;
  optimalLow: number;
  optimalHigh: number;
  refLow?: number;
  refHigh?: number;
  status: string;
}

interface BiomarkerRangeChartProps {
  markers: MarkerData[];
}

function SingleRange({ marker }: { marker: MarkerData }) {
  // Determine the full range for the bar
  // Use ref range if available, otherwise derive from optimal range with padding
  const padding = (marker.optimalHigh - marker.optimalLow) * 0.5;
  const rangeMin = marker.refLow ?? Math.max(0, marker.optimalLow - padding);
  const rangeMax = marker.refHigh ?? marker.optimalHigh + padding;
  const fullRange = rangeMax - rangeMin;

  // Clamp value for display while still showing the actual number
  const displayValue = marker.value;
  const clampedValue = Math.min(Math.max(marker.value, rangeMin), rangeMax);

  // Calculate positions as percentages
  const optimalLeftPct = ((marker.optimalLow - rangeMin) / fullRange) * 100;
  const optimalWidthPct =
    ((marker.optimalHigh - marker.optimalLow) / fullRange) * 100;
  const markerPct = ((clampedValue - rangeMin) / fullRange) * 100;

  const color = statusColor(marker.status);

  return (
    <div className="flex items-center gap-4">
      <div className="w-32 shrink-0 text-right">
        <span className="text-sm font-medium text-navy">{marker.name}</span>
      </div>
      <div className="relative flex-1">
        {/* Full reference range bar */}
        <div className="h-3 w-full rounded-full bg-gray-200">
          {/* Optimal zone overlay */}
          <div
            className="absolute top-0 h-3 rounded-full bg-emerald-200"
            style={{
              left: `${optimalLeftPct}%`,
              width: `${optimalWidthPct}%`,
            }}
          />
          {/* Value marker */}
          <motion.div
            className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md"
            style={{ backgroundColor: color }}
            initial={{ left: '0%', opacity: 0 }}
            animate={{ left: `${markerPct}%`, opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
          />
        </div>
        {/* Labels below */}
        <div className="mt-1.5 flex items-center justify-between text-[10px] text-gray-400">
          <span>{rangeMin}</span>
          <span>{rangeMax}</span>
        </div>
      </div>
      <div className="w-28 shrink-0">
        <span className="text-sm font-semibold" style={{ color }}>
          {displayValue} {marker.unit}
        </span>
        <div className="text-[10px]" style={{ color }}>
          {statusLabel(marker.status)}
        </div>
      </div>
    </div>
  );
}

export function BiomarkerRangeChart({ markers }: BiomarkerRangeChartProps) {
  return (
    <div className="space-y-4">
      {markers.map((marker) => (
        <SingleRange key={marker.key} marker={marker} />
      ))}
    </div>
  );
}
